import { Box, Button, Chip, Grid, Paper, Stack, Typography } from "@mui/material";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import HelpOutlineRounded from "@mui/icons-material/HelpOutlineRounded";
import RuleFolderOutlined from "@mui/icons-material/RuleFolderOutlined";
import ViewKanbanOutlined from "@mui/icons-material/ViewKanbanOutlined";
import GroupOutlined from "@mui/icons-material/GroupOutlined";
import TaskAltRounded from "@mui/icons-material/TaskAltRounded";
import { NavLink } from "react-router-dom";

const sections = [
  {
    title: "Estrutura de processos",
    icon: RuleFolderOutlined,
    items: [
      "Um processo pode conter subprocessos ou tarefas, mas nunca os dois ao mesmo tempo.",
      "A profundidade maxima da arvore e de 3 niveis.",
      "Subprocessos sempre nascem copiando o workflow do processo pai.",
      "Processos do tipo INTERNA podem ser visualizados, mas o cliente nao pode editar nem excluir.",
    ],
  },
  {
    title: "Workflow e conclusao",
    icon: ViewKanbanOutlined,
    items: [
      "Cada pasta possui seu proprio workflow e cada etapa vira uma coluna do kanban.",
      "Nao existe status separado: a conclusao da tarefa depende da etapa do workflow.",
      "Ao mover uma tarefa para uma etapa conclusiva, ela passa a ser concluida automaticamente.",
      "Ao sair de uma etapa conclusiva, a tarefa volta a ficar em aberto.",
    ],
  },
  {
    title: "Pessoas na tarefa",
    icon: GroupOutlined,
    items: [
      "Toda tarefa possui 1 responsavel principal.",
      "A tarefa pode ter varios acompanhantes.",
      "Responsavel e acompanhantes sempre vem da base de usuarios do cliente.",
      "O responsavel principal nao deve ser duplicado como acompanhante.",
    ],
  },
  {
    title: "Rotina de operacao",
    icon: TaskAltRounded,
    items: [
      "Crie primeiro a estrutura do processo e depois ajuste as etapas do workflow.",
      "Use prioridades e prazos para destacar o que precisa de atencao imediata.",
      "Acompanhe a execucao pelo kanban e mova as tarefas conforme avancam.",
      "Mantenha responsavel e acompanhantes atualizados para dar visibilidade ao time.",
    ],
  },
];

const TaskAgentAjuda = () => {
  return (
    <Stack spacing={3}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          borderRadius: { xs: 3, md: 4 },
          border: (theme) => `1px solid ${theme.palette.divider}`,
          bgcolor: "background.paper",
          boxShadow: {
            xs: "0 2px 10px rgba(0,0,0,0.08)",
            md: "0 4px 20px rgba(0,0,0,0.08)",
          },
        }}
      >
        <Stack spacing={3} alignItems="center" textAlign="center">
          <Stack direction="row" spacing={1.2} alignItems="center" justifyContent="center">
            <Box
              sx={{
                width: 58,
                height: 58,
                borderRadius: 2.5,
                display: "grid",
                placeItems: "center",
                position: "relative",
                bgcolor: (theme) =>
                  theme.palette.mode === "dark" ? "rgba(14, 21, 38, 0.94)" : "rgba(248, 250, 255, 0.98)",
                border: "1px solid rgba(102, 126, 234, 0.24)",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), 0 10px 24px rgba(16,24,40,0.08)",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  borderRadius: 2.5,
                  padding: "1px",
                  background: "linear-gradient(135deg, rgba(102,126,234,0.65), rgba(118,75,162,0.45))",
                  WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                  WebkitMaskComposite: "xor",
                  maskComposite: "exclude",
                  pointerEvents: "none",
                },
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: 10,
                  left: 10,
                  width: 18,
                  height: 3,
                  borderRadius: 999,
                  background: "linear-gradient(90deg, #667eea, #764ba2)",
                  pointerEvents: "none",
                },
              }}
            >
              <HelpOutlineRounded sx={{ fontSize: 30, color: "primary.main" }} />
            </Box>
            <Typography
              variant="h4"
              fontWeight={800}
              sx={{
                letterSpacing: -0.8,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Ajuda do TaskAgent
            </Typography>
          </Stack>

          <Stack spacing={1.5} alignItems="center" sx={{ maxWidth: 860 }}>
            <Typography
              variant="h4"
              fontWeight={800}
              sx={{
                color: "text.primary",
                letterSpacing: -0.9,
                fontSize: { xs: "1.8rem", md: "2.4rem" },
              }}
            >
              Entenda a estrutura do produto e opere com mais clareza.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                fontSize: { xs: "1rem", md: "1.08rem" },
                lineHeight: 1.65,
                maxWidth: 760,
              }}
            >
              Este guia resume as principais regras do TaskAgent para ajudar seu time a organizar processos,
              tarefas, etapas, prioridades e responsabilidades no dia a dia.
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" justifyContent="center">
            <Chip label="Processos" size="medium" sx={{ bgcolor: "#e8f5e8", color: "#2e7d32", fontWeight: 500 }} />
            <Chip label="Workflow" size="medium" sx={{ bgcolor: "#eef2ff", color: "#4355b9", fontWeight: 500 }} />
            <Chip label="Kanban" size="medium" sx={{ bgcolor: "#f3e8ff", color: "#7b3fc6", fontWeight: 500 }} />
            <Chip label="Responsaveis" size="medium" sx={{ bgcolor: "#f5f5f5", color: "#616161", fontWeight: 500 }} />
          </Stack>

          <Button
            component={NavLink}
            to="/task-agent/inicio"
            variant="outlined"
            startIcon={<ArrowBackRounded />}
            sx={{
              px: 2.6,
              py: 1.05,
              borderRadius: 3,
              color: "text.primary",
              borderColor: "rgba(102, 126, 234, 0.24)",
              bgcolor: "rgba(248,250,255,0.9)",
              "&:hover": {
                borderColor: "rgba(102, 126, 234, 0.38)",
                bgcolor: "rgba(238,242,255,0.95)",
              },
            }}
          >
            Voltar para o inicio
          </Button>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3 },
          borderRadius: 3,
          border: "1px solid rgba(102, 126, 234, 0.16)",
          bgcolor: "rgba(238,242,255,0.65)",
        }}
      >
        <Stack spacing={0.75}>
          <Typography variant="subtitle1" fontWeight={700} color="text.primary">
            Como aproveitar melhor esta area
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
            Use esta pagina como referencia rapida de regras operacionais. A tela de inicio do TaskAgent fica mais
            comercial e orientada ao valor do produto, enquanto aqui a ideia e facilitar a consulta no uso diario.
          </Typography>
        </Stack>
      </Paper>

      <Grid container spacing={2.25}>
        {sections.map((section) => {
          const Icon = section.icon;

          return (
            <Grid item xs={12} md={6} key={section.title}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 4,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  height: "100%",
                  boxShadow: "0 2px 14px rgba(15, 23, 42, 0.05)",
                  transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 10px 26px rgba(15, 23, 42, 0.09)",
                    borderColor: "rgba(102, 126, 234, 0.22)",
                  },
                }}
              >
                <Stack spacing={2.25}>
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: 3,
                      display: "grid",
                      placeItems: "center",
                      bgcolor: "rgba(102,126,234,0.08)",
                      color: "primary.main",
                      border: "1px solid rgba(102,126,234,0.12)",
                    }}
                  >
                    <Icon />
                  </Box>

                  <Stack spacing={1}>
                    <Typography variant="h6" fontWeight={700}>
                      {section.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {section.title === "Estrutura de processos" &&
                        "Regras para montar a arvore de processos e manter a organizacao da operacao."}
                      {section.title === "Workflow e conclusao" &&
                        "Como as etapas controlam o andamento e a conclusao das tarefas no kanban."}
                      {section.title === "Pessoas na tarefa" &&
                        "Definicoes sobre responsavel principal, acompanhantes e visibilidade da execucao."}
                      {section.title === "Rotina de operacao" &&
                        "Boas praticas para organizar a execucao e facilitar o acompanhamento do time."}
                    </Typography>
                  </Stack>

                  <Stack spacing={1.2}>
                    {section.items.map((item) => (
                      <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            mt: 0.8,
                            borderRadius: "50%",
                            flexShrink: 0,
                            bgcolor: "primary.main",
                          }}
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                          {item}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Stack>
  );
};

export default TaskAgentAjuda;
